import { ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './Typography.module.css';

// The Symphony type scale as a component. The size/weight/tracking live on the
// global `.sym-h1`…`.sym-p3` classes (styles/typography.css, the single source of
// truth); this wrapper adds the MUI-style layout knobs — color, alignment,
// truncation, bottom gutter — and lets the caller override the rendered element.
//
// `heading-02`, `display-05` and `tiny-semi-bold` are the DS roles with no app-facing `.sym-h*` alias,
// so they map straight onto their generated role classes. The legacy scale jumps 20→16
// and has no 18/500 step (`heading-02`), and its only 16px step is the UI face — it has
// no Archivo 16 at all (`display-05`), which is the role both phone apps call `display05`.
export type TypographyVariant =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'heading-02'
  | 'heading-04'
  | 'display-05'
  | 'small-medium'
  | 'tiny-medium'
  | 'tiny-semi-bold'
  | 'p1'
  | 'p2'
  | 'p3';
export type TypographyColor =
  | 'primary'
  | 'strong'
  | 'default'
  | 'secondary'
  | 'muted'
  | 'neutral'
  | 'neutralStrong'
  | 'accent'
  | 'inherit';

// Sensible default element per variant — overridable via `component`.
const VARIANT_ELEMENT: Record<TypographyVariant, ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  'heading-02': 'h3',
  'heading-04': 'p',
  'display-05': 'h3',
  'small-medium': 'span',
  'tiny-medium': 'span',
  'tiny-semi-bold': 'span',
  p1: 'p',
  p2: 'p',
  p3: 'p',
};

// Most variants resolve to `.sym-<variant>`; the role-named ones point at their
// generated `.sym-type-<role>` class directly.
const VARIANT_CLASS: Partial<Record<TypographyVariant, string>> = {
  'heading-02': 'sym-type-heading-02',
  // Small/Regular at the DS's 14/20 — the generated role, which the legacy `.sym-*` scale has no
  // alias for (its own 14s are `h4` 14/18 SemiBold and `p2` 14/18 Regular).
  'heading-04': 'sym-type-heading-04',
  'display-05': 'sym-type-display-05',
  // Small at the Medium tier — 14/18, the role the connectors rail group titles use.
  'small-medium': 'sym-type-small-medium',
  // Tiny at the Medium tier — 12/16 (Figma Tiny/Medium), the brief card's eyebrow + agent line.
  'tiny-medium': 'sym-type-tiny-medium',
  // Tiny at the SemiBold tier — the legacy scale's only 12 is `p3`, which is Regular.
  'tiny-semi-bold': 'sym-type-tiny-semi-bold',
};

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  align?: 'start' | 'center' | 'end';
  /** Clamp to one line with an ellipsis (MUI `noWrap`). */
  noWrap?: boolean;
  /** Adds bottom margin so stacked headings/paragraphs breathe (MUI `gutterBottom`). */
  gutterBottom?: boolean;
  /** Render as a different element (e.g. a <span> label, a <label>). */
  component?: ElementType;
  children?: ReactNode;
}

export function Typography({
  variant = 'p2',
  color = 'default',
  align,
  noWrap,
  gutterBottom,
  component,
  className,
  children,
  ...rest
}: TypographyProps) {
  const Component = component ?? VARIANT_ELEMENT[variant];
  const classes = [
    VARIANT_CLASS[variant] ?? `sym-${variant}`,
    styles.root,
    styles[`color-${color}`],
    align && styles[`align-${align}`],
    noWrap && styles.noWrap,
    gutterBottom && styles.gutterBottom,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
